/**
 * PlanningNotifTest — standalone test component for the planning notification flow.
 *
 * USAGE:
 *   1. Navigate to /#planning-test in your browser (both users).
 *   2. User 1 fills in the connection ID (54WEKAWvDLKKmMumgTB8) and clicks "Stuur notificatie".
 *   3. User 2 should see it appear immediately in the "Ontvangen" list.
 *
 * This tests ONLY the Firestore write + listener, nothing else.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  doc,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';

interface NotifDoc {
  id: string;
  type: string;
  fromUserId: string;
  fromUserName: string;
  connectionId: string;
  read: boolean;
  createdAt: string;
}

const PlanningNotifTest: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<{ uid: string; email: string | null; displayName: string | null } | null>(null);
  const [connId, setConnId] = useState('54WEKAWvDLKKmMumgTB8');
  const [targetUserId, setTargetUserId] = useState('');
  const [sending, setSending] = useState(false);
  const [sendLog, setSendLog] = useState<string[]>([]);
  const [received, setReceived] = useState<NotifDoc[]>([]);
  const [listenerLog, setListenerLog] = useState<string[]>([]);

  const appendSend = (msg: string) => setSendLog(prev => [`[${new Date().toISOString().slice(11, 23)}] ${msg}`, ...prev]);
  const appendListener = (msg: string) => setListenerLog(prev => [`[${new Date().toISOString().slice(11, 23)}] ${msg}`, ...prev]);

  // Auth
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (u) {
        setCurrentUser({ uid: u.uid, email: u.email, displayName: u.displayName });
        appendListener(`Auth: ingelogd als ${u.email} (uid: ${u.uid.slice(0, 8)}..., displayName: "${u.displayName}")`);
      } else {
        setCurrentUser(null);
        appendListener('Auth: niet ingelogd');
      }
    });
  }, []);

  // Listener for incoming notifications
  useEffect(() => {
    if (!currentUser?.uid) return;

    appendListener(`Listener gestart voor userId: ${currentUser.uid.slice(0, 8)}...`);

    const q = query(
      collection(db, 'planningNotifications'),
      where('toUserId', '==', currentUser.uid),
      where('read', '==', false)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      appendListener(`Snapshot ontvangen: ${snapshot.docChanges().length} wijziging(en), totaal docs: ${snapshot.size}`);

      snapshot.docChanges().forEach(async (change) => {
        const data = change.doc.data();
        appendListener(`  → type: ${change.type}, notif.type: ${data.type}, from: ${data.fromUserName}, connId: ${data.connectionId}`);

        if (change.type === 'added') {
          setReceived(prev => [{
            id: change.doc.id,
            type: data.type,
            fromUserId: data.fromUserId,
            fromUserName: data.fromUserName,
            connectionId: data.connectionId,
            read: data.read,
            createdAt: data.createdAt?.toDate?.()?.toISOString?.() || 'pending...',
          }, ...prev]);

          // Mark as read
          try {
            await updateDoc(doc(db, 'planningNotifications', change.doc.id), { read: true });
            appendListener(`  ✅ Gemarkeerd als gelezen: ${change.doc.id}`);
          } catch (e) {
            appendListener(`  ❌ Fout bij markeren: ${e}`);
          }
        }
      });
    }, (err) => {
      appendListener(`❌ Listener error: ${err.message} (code: ${err.code})`);
    });

    return () => {
      appendListener('Listener gestopt');
      unsub();
    };
  }, [currentUser?.uid]);

  // Send test notification
  const handleSend = async () => {
    if (!currentUser?.uid) { appendSend('❌ Niet ingelogd'); return; }
    if (!targetUserId.trim()) { appendSend('❌ Vul een target userId in'); return; }
    if (!connId.trim()) { appendSend('❌ Vul een connectionId in'); return; }

    const displayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Gebruiker';
    setSending(true);
    appendSend(`Sturen naar: ${targetUserId.slice(0, 8)}... van: ${displayName} (${currentUser.uid.slice(0, 8)}...)`);

    try {
      const docRef = await addDoc(collection(db, 'planningNotifications'), {
        toUserId: targetUserId.trim(),
        fromUserId: currentUser.uid,
        fromUserName: displayName,
        connectionId: connId.trim(),
        type: 'planning_started',
        createdAt: serverTimestamp(),
        read: false,
      });
      appendSend(`✅ Geschreven! doc id: ${docRef.id}`);
    } catch (e: unknown) {
      appendSend(`❌ Schrijffout: ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ fontFamily: 'monospace', padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ color: '#4f46e5' }}>🧪 Planning Notificatie Test</h2>
      <p style={{ color: '#666' }}>Test de Firestore write + listener los van ConnectieFlow.</p>

      {/* Auth status */}
      <div style={{ background: currentUser ? '#d1fae5' : '#fee2e2', padding: 10, borderRadius: 6, marginBottom: 16 }}>
        {currentUser
          ? `✅ Ingelogd: ${currentUser.email} | uid: ${currentUser.uid} | displayName: "${currentUser.displayName}"`
          : '❌ Niet ingelogd — log in via de normale app'}
      </div>

      {/* Your UID (copy to other user) */}
      {currentUser && (
        <div style={{ background: '#fffbeb', padding: 10, borderRadius: 6, marginBottom: 16, border: '1px solid #fbbf24' }}>
          <strong>Jouw userId (kopieer dit naar het targetUserId veld van de andere user):</strong>
          <br />
          <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{currentUser.uid}</code>
        </div>
      )}

      {/* Send form */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>📤 Stuur notificatie (User 1)</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label>
            Target userId (User 2's uid):
            <input
              value={targetUserId}
              onChange={e => setTargetUserId(e.target.value)}
              placeholder="ovw1sqz2lsX3oWNwEqpfORfhFs42"
              style={{ width: '100%', padding: '6px 8px', fontFamily: 'monospace', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 4 }}
            />
          </label>
          <label>
            Connection ID:
            <input
              value={connId}
              onChange={e => setConnId(e.target.value)}
              style={{ width: '100%', padding: '6px 8px', fontFamily: 'monospace', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 4 }}
            />
          </label>
          <button
            onClick={handleSend}
            disabled={sending || !currentUser}
            style={{
              background: sending ? '#94a3b8' : '#4f46e5',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: 6,
              cursor: sending ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              alignSelf: 'flex-start',
            }}
          >
            {sending ? 'Bezig...' : '🚀 Stuur notificatie'}
          </button>
        </div>

        {/* Send log */}
        <div style={{ marginTop: 12, background: '#1e293b', color: '#e2e8f0', padding: 10, borderRadius: 4, maxHeight: 120, overflow: 'auto', fontSize: 12 }}>
          {sendLog.length === 0 ? <span style={{ color: '#64748b' }}>Geen activiteit...</span> : sendLog.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>

      {/* Received notifications */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>📥 Ontvangen notificaties (User 2)</h3>
        {received.length === 0
          ? <p style={{ color: '#94a3b8', margin: 0 }}>Nog niets ontvangen...</p>
          : received.map(n => (
            <div key={n.id} style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <strong>Type:</strong> {n.type}<br />
              <strong>Van:</strong> {n.fromUserName} ({n.fromUserId.slice(0, 8)}...)<br />
              <strong>Connectie:</strong> {n.connectionId}<br />
              <strong>Tijdstip:</strong> {n.createdAt}
            </div>
          ))}

        {/* Listener log */}
        <div style={{ marginTop: 12, background: '#1e293b', color: '#e2e8f0', padding: 10, borderRadius: 4, maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
          <div style={{ color: '#94a3b8', marginBottom: 4 }}>Listener log:</div>
          {listenerLog.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
};

export default PlanningNotifTest;
